<template>
  <!-- Hero Section -->
  <main id="home" class="container text-center section-py" style="padding-top: 10rem;">
    <h1 class="display-3 display-md-1 fw-bolder text-dark lh-tight">
      {{ t('pages./superpowers.hero.title') }} <span class="gradient-text">{{ t('pages./superpowers.hero.title-highlight') }}</span><br>{{ t('pages./superpowers.hero.title-suffix') }}
    </h1>
    <p class="lead text-secondary mx-auto mt-4" style="max-width: 650px;">
      {{ t('pages./superpowers.hero.subtitle') }}
    </p>
    <div class="mt-5 d-flex justify-content-center">
      <template v-if="!authStore.isLoggedIn">
        <a :href="`/${$i18n.locale}/signup`" class="landing-btn landing-btn-lg">
          {{ t('pages./superpowers.hero.cta') }}
        </a>
      </template>
      <template v-else>
        <a href="/app" class="landing-btn landing-btn-lg">
          {{ t('common.enter-dashboard') }}
        </a>
      </template>
    </div>
  </main>


  <!-- Use Cases Section -->
  <section id="use-cases" class="section-py">
    <div class="container">
      <div class="text-center">
        <h2 class="display-5 fw-bold text-dark">{{ t('pages./superpowers.use-cases.title') }}</h2>
        <p class="lead text-secondary mx-auto mt-3" style="max-width: 650px;">{{ t('pages./superpowers.use-cases.subtitle') }}</p>
      </div>
      <div class="mx-auto mt-5" style="max-width: 1024px;">
        <ul class="nav nav-pills justify-content-center flex-wrap gap-2" id="pills-tab" role="tablist">
          <li class="nav-item" role="presentation">
            <button class="nav-link px-3 py-2" :class="{ active: activeTab === 'finance' }" @click="activeTab = 'finance'" type="button">{{ t('pages./superpowers.use-cases.tabs.finance') }}</button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link px-3 py-2" :class="{ active: activeTab === 'hr' }" @click="activeTab = 'hr'" type="button">{{ t('pages./superpowers.use-cases.tabs.hr') }}</button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link px-3 py-2" :class="{ active: activeTab === 'sales' }" @click="activeTab = 'sales'" type="button">{{ t('pages./superpowers.use-cases.tabs.sales') }}</button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link px-3 py-2" :class="{ active: activeTab === 'service' }" @click="activeTab = 'service'" type="button">{{ t('pages./superpowers.use-cases.tabs.service') }}</button>
          </li>
          <!-- <li class="nav-item" role="presentation">
            <button class="nav-link px-3 py-2" :class="{ active: activeTab === 'mfg' }" @click="activeTab = 'mfg'" type="button">{{ t('pages./superpowers.use-cases.tabs.mfg') }}</button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link px-3 py-2" :class="{ active: activeTab === 'it' }" @click="activeTab = 'it'" type="button">{{ t('pages./superpowers.use-cases.tabs.it') }}</button>
          </li> -->
        </ul>
        <div class="mt-5">
          <!-- Tab 1: 財務與行政 -->
          <div v-show="activeTab === 'finance'" class="tab-content">
            <div class="row g-5 align-items-start">
              <div class="col-md-6 order-md-2">
                <h3 class="fs-2 fw-bold">{{ t('pages./superpowers.use-cases.finance.title') }}</h3>
                <p class="mt-3 text-secondary" v-html="t('pages./superpowers.use-cases.finance.description')"></p>
                <!-- AI Query Highlight section commented out -->
                <!--
                <div class="mt-4 bg-light p-3 rounded-3">
                  <p class="fw-semibold text-dark">{{ t('pages./superpowers.use-cases.ai-prefix') }}</p>
                  <p class="mt-2 text-primary font-monospace small">{{ t('pages./superpowers.use-cases.finance.ai-highlight') }}</p>
                </div>
                -->
                <!-- 財務費用申請流程圖 -->
                <div class="finance-expense-flowchart mt-4">
                  <div class="flowchart-container d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-2">
                    <!-- 步驟 1: 員工提交請款表單（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-file-invoice-dollar" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.finance.flowchart.step1') }}</h5>
                    </div>

                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 2: 直屬主管審批（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-user-check" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.finance.flowchart.step2') }}</h5>
                    </div>

                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 3: AI 會計科目分類（紫色） -->
                    <div class="flowchart-node flowchart-node-purple flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-robot" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.finance.flowchart.step3') }}</h5>
                    </div>

                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 4: 財務部門批准（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-check-circle" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.finance.flowchart.step4') }}</h5>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6 order-md-1">
                <!-- 財務費用申請表單 -->
                <div class="finance-expense-form-preview">
                  <div class="bg-white p-4 rounded-3 shadow-sm border">
                    <div class="text-center mb-4">
                      <h4 class="h5 fw-bold text-primary mb-2">{{ t('pages./superpowers.use-cases.finance.form.title') }}</h4>
                    </div>

                    <form>
                      <!-- 申請人姓名與所屬部門 並列顯示 -->
                      <div class="row mb-3">
                        <div class="col-md-6">
                          <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.finance.form.employee-name') }}</label>
                          <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.finance.form.sample-name')" readonly>
                        </div>
                        <div class="col-md-6">
                          <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.finance.form.department') }}</label>
                          <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.finance.form.sample-department')" readonly>
                        </div>
                      </div>

                      <!-- 申請金額 -->
                      <div class="mb-3">
                        <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.finance.form.amount') }}</label>
                        <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.finance.form.sample-amount')" readonly>
                      </div>

                      <!-- 費用說明 -->
                      <div class="mb-3">
                        <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.finance.form.description') }}</label>
                        <textarea class="form-control" readonly>{{ t('pages./superpowers.use-cases.finance.form.sample-description') }}</textarea>
                      </div>

                      <!-- 附件上傳區域 (顯示用) -->
                      <div class="mb-3">
                        <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.finance.form.attachment-upload') }}</label>
                        <div class="border rounded-3 p-3 bg-light text-center">
                          <i class="fas fa-file-invoice text-muted me-2"></i>
                          <span class="text-muted small">{{ t('pages./superpowers.use-cases.finance.form.sample-attachment') }}</span>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Tab 2: 人資與招募 -->
          <div v-show="activeTab === 'hr'" class="tab-content">
            <div class="row g-5 align-items-start">
              <div class="col-md-6 order-md-2">
                <h3 class="fs-2 fw-bold">{{ t('pages./superpowers.use-cases.hr.title') }}</h3>
                <p class="mt-3 text-secondary" v-html="t('pages./superpowers.use-cases.hr.description')"></p>
                <!-- AI Query Highlight section commented out -->
                <!--
                <div class="mt-4 bg-light p-3 rounded-3">
                  <p class="fw-semibold text-dark">{{ t('pages./superpowers.use-cases.ai-prefix') }}</p>
                  <p class="mt-2 text-primary font-monospace small">{{ t('pages./superpowers.use-cases.hr.ai-highlight') }}</p>
                </div>
                -->
                <!-- 人資招募流程圖 -->
                <div class="hr-recruitment-flowchart mt-4">
                  <div class="flowchart-container d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-2">
                    <!-- 步驟 1: 應徵者發起表單（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-user-plus" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.hr.flowchart.step1') }}</h5>
                    </div>
                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 2: AI 審閱並總結（紫色） -->
                    <div class="flowchart-node flowchart-node-purple flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-robot" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.hr.flowchart.step2') }}</h5>
                    </div>
                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 3: 人資部門評選（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-user-tie" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.hr.flowchart.step3') }}</h5>
                    </div>
                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 4: 用人主管面試（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-handshake" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.hr.flowchart.step4') }}</h5>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6 order-md-1">
                <!-- 人資應徵表單 -->
                <div class="hr-application-form-preview">
                  <div class="bg-white p-4 rounded-3 shadow-sm border">
                    <div class="text-center mb-4">
                      <h4 class="h5 fw-bold text-primary mb-2">{{ t('pages./superpowers.use-cases.hr.form.title') }}</h4>
                    </div>

                    <form>
                      <!-- 應徵者姓名與聯絡電話 並列顯示 -->
                      <div class="row mb-3">
                        <div class="col-md-6">
                          <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.hr.form.applicant-name') }}</label>
                          <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.hr.form.sample-name')" readonly>
                        </div>
                        <div class="col-md-6">
                          <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.hr.form.phone') }}</label>
                          <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.hr.form.sample-phone')" readonly>
                        </div>
                      </div>

                      <!-- 應徵職位 -->
                      <div class="mb-3">
                        <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.hr.form.position') }}</label>
                        <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.hr.form.sample-position')" readonly>
                      </div>

                      <!-- 工作經驗年數 -->
                      <div class="mb-3">
                        <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.hr.form.experience') }}</label>
                        <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.hr.form.sample-experience')" readonly>
                      </div>

                      <!-- 履歷上傳區域 (顯示用) -->
                      <div class="mb-3">
                        <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.hr.form.resume-upload') }}</label>
                        <div class="border rounded-3 p-3 bg-light text-center">
                          <i class="fas fa-file-pdf text-muted me-2"></i>
                          <span class="text-muted small">{{ t('pages./superpowers.use-cases.hr.form.sample-resume') }}</span>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Tab 3: 業務與訂單 -->
          <div v-show="activeTab === 'sales'" class="tab-content">
            <div class="row g-5 align-items-start">
              <div class="col-md-6 order-md-2">
                <h3 class="fs-2 fw-bold">{{ t('pages./superpowers.use-cases.sales.title') }}</h3>
                <p class="mt-3 text-secondary" v-html="t('pages./superpowers.use-cases.sales.description')"></p>
                <!-- AI Query Highlight section commented out -->
                <!--
                <div class="mt-4 bg-light p-3 rounded-3">
                  <p class="fw-semibold text-dark">{{ t('pages./superpowers.use-cases.ai-prefix') }}</p>
                  <p class="mt-2 text-primary font-monospace small">{{ t('pages./superpowers.use-cases.sales.ai-highlight') }}</p>
                </div>
                -->
                <!-- 業務報價流程圖 -->
                <div class="sales-quote-flowchart mt-4">
                  <div class="flowchart-container d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-2">
                    <!-- 步驟 1（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-file-alt" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.sales.flowchart.step1') }}</h5>
                    </div>
                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 2: AI（紫色） -->
                    <div class="flowchart-node flowchart-node-purple flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-robot" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.sales.flowchart.step2') }}</h5>
                    </div>
                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 3（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-user-check" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.sales.flowchart.step3') }}</h5>
                    </div>
                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 4（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-shipping-fast" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.sales.flowchart.step4') }}</h5>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6 order-md-1">
                <!-- 業務報價申請表單 -->
                <div class="sales-quote-form-preview">
                  <div class="bg-white p-4 rounded-3 shadow-sm border">
                    <div class="text-center mb-4">
                      <h4 class="h5 fw-bold text-primary mb-2">{{ t('pages./superpowers.use-cases.sales.form.title') }}</h4>
                    </div>

                    <form>
                      <!-- 業務姓名與客戶名稱 並列顯示 -->
                      <div class="row mb-3">
                        <div class="col-md-6">
                          <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.sales.form.sales-name') }}</label>
                          <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.sales.form.sample-sales-name')" readonly>
                        </div>
                        <div class="col-md-6">
                          <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.sales.form.customer-name') }}</label>
                          <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.sales.form.sample-customer-name')" readonly>
                        </div>
                      </div>

                      <!-- 產品名稱 -->
                      <div class="mb-3">
                        <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.sales.form.product-name') }}</label>
                        <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.sales.form.sample-product-name')" readonly>
                      </div>

                      <!-- 數量與單價 並列顯示 -->
                      <div class="row mb-3">
                        <div class="col-md-6">
                          <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.sales.form.quantity') }}</label>
                          <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.sales.form.sample-quantity')" readonly>
                        </div>
                        <div class="col-md-6">
                          <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.sales.form.unit-price') }}</label>
                          <input type="text" class="form-control" :value="t('pages./superpowers.use-cases.sales.form.sample-unit-price')" readonly>
                        </div>
                      </div>

                      <!-- 總金額 -->
                      <div class="mb-3">
                        <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.sales.form.total-amount') }}</label>
                        <input type="text" class="form-control fw-bold text-success" :value="t('pages./superpowers.use-cases.sales.form.sample-total-amount')" readonly>
                      </div>

                      <!-- 備註說明 -->
                      <div class="mb-3">
                        <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.sales.form.notes') }}</label>
                        <textarea class="form-control" readonly>{{ t('pages./superpowers.use-cases.sales.form.sample-notes') }}</textarea>
                      </div>

                      <!-- 附件上傳區域 (顯示用) -->
                      <div class="mb-3">
                        <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.use-cases.sales.form.related-documents') }}</label>
                        <div class="border rounded-3 p-3 bg-light text-center">
                          <i class="fas fa-file-pdf text-muted me-2"></i>
                          <span class="text-muted small">{{ t('pages./superpowers.use-cases.sales.form.sample-document') }}</span>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
           <!-- Tab 4: 客戶服務 -->
          <div v-show="activeTab === 'service'" class="tab-content">
            <div class="row g-5 align-items-start">
              <div class="col-md-6 order-md-2">
                <h3 class="fs-2 fw-bold">{{ t('pages./superpowers.use-cases.service.title') }}</h3>
                <p class="mt-3 text-secondary" v-html="t('pages./superpowers.use-cases.service.description')"></p>
                <!-- <div class="mt-4 bg-light p-3 rounded-3">
                  <p class="fw-semibold text-dark">{{ t('pages./superpowers.use-cases.ai-prefix') }}</p>
                  <p class="mt-2 text-primary font-monospace small">{{ t('pages./superpowers.use-cases.service.ai-highlight') }}</p>
                </div> -->
                <!-- 客戶服務流程圖 -->
                <div class="customer-service-flowchart mt-4">
                  <div class="flowchart-container d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-2">
                    <!-- 步驟 1（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-user" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.service.flowchart.step1') }}</h5>
                    </div>
                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 2: AI（紫色） -->
                    <div class="flowchart-node flowchart-node-purple flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-robot" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.service.flowchart.step2') }}</h5>
                    </div>
                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 3（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-users" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.service.flowchart.step3') }}</h5>
                    </div>
                    <div class="arrow-mobile text-center d-md-none"><i class="fas fa-arrow-down text-muted"></i></div>
                    <div class="arrow-desktop align-self-center d-none d-md-flex"><i class="fas fa-arrow-right text-muted"></i></div>

                    <!-- 步驟 4（藍色） -->
                    <div class="flowchart-node flowchart-node-blue flex-fill text-center p-3 rounded-3 border">
                      <div class="icon-wrapper text-white rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                        <i class="fas fa-archive" style="font-size: 18px;"></i>
                      </div>
                      <h5 class="small fw-semibold mb-1">{{ t('pages./superpowers.use-cases.service.flowchart.step4') }}</h5>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6 order-md-1">
                <!-- 客戶服務表單 -->
                <div class="customer-service-preview">
                  <div class="bg-white p-4 rounded-3 shadow-sm border">
                    <div class="text-center mb-4">
                      <h4 class="h5 fw-bold text-primary mb-2">{{ t('pages./superpowers.use-cases.service.form.title') }}</h4>
                    </div>
                    
                    <div class="form-preview">
                      <!-- 客戶資訊 -->
                      <div class="row g-3">
                        <div class="col-md-6">
                          <label class="form-label small text-secondary fw-medium">{{ t('pages./superpowers.use-cases.service.form.customer-name') }}</label>
                          <div class="form-control form-control-sm bg-light text-muted">{{ t('pages./superpowers.use-cases.service.form.sample-name') }}</div>
                        </div>
                        <div class="col-md-6">
                          <label class="form-label small text-secondary fw-medium">{{ t('pages./superpowers.use-cases.service.form.contact-phone') }}</label>
                          <div class="form-control form-control-sm bg-light text-muted">{{ t('pages./superpowers.use-cases.service.form.sample-phone') }}</div>
                        </div>
                      </div>
                      
                      <!-- 服務類型 -->
                      <div class="mt-3">
                        <label class="form-label small text-secondary fw-medium">{{ t('pages./superpowers.use-cases.service.form.service-type') }}</label>
                        <div class="form-control form-control-sm bg-light text-muted">
                          <i class="fas fa-exclamation-triangle text-warning me-2"></i>{{ t('pages./superpowers.use-cases.service.form.service-complaint') }}
                        </div>
                      </div>
                      
                      <!-- 問題描述 -->
                      <div class="mt-3">
                        <label class="form-label small text-secondary fw-medium">{{ t('pages./superpowers.use-cases.service.form.problem-description') }}</label>
                        <div class="form-control bg-light text-muted" style="height: 80px; font-size: 0.875rem; line-height: 1.4;">
                          {{ t('pages./superpowers.use-cases.service.form.sample-description') }}
                        </div>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Tab 5: 生產與製造 -->
          <!-- <div v-show="activeTab === 'mfg'" class="tab-content">
             <div class="row g-5 align-items-center">
              <div class="col-md-6 order-md-2">
                <h3 class="fs-2 fw-bold">{{ t('pages./superpowers.use-cases.mfg.title') }}</h3>
                <p class="mt-3 text-secondary" v-html="t('pages./superpowers.use-cases.mfg.description')"></p>
                <div class="mt-4 bg-light p-3 rounded-3">
                  <p class="fw-semibold text-dark">{{ t('pages./superpowers.use-cases.ai-prefix') }}</p>
                  <p class="mt-2 text-primary font-monospace small">{{ t('pages./superpowers.use-cases.mfg.ai-highlight') }}</p>
                </div>
              </div>
              <div class="col-md-6 order-md-1">
                <img src="https://placehold.co/600x400/EEF2FF/4F46E5?text=生產排程示意圖" alt="生產排程與進度追蹤示意圖" class="img-fluid rounded-3 shadow-sm">
              </div>
            </div>
          </div> -->
          <!-- Tab 6: 資訊與服務 -->
          <!-- <div v-show="activeTab === 'it'" class="tab-content">
            <div class="row g-5 align-items-center">
              <div class="col-md-6">
                <h3 class="fs-2 fw-bold">{{ t('pages./superpowers.use-cases.it.title') }}</h3>
                <p class="mt-3 text-secondary" v-html="t('pages./superpowers.use-cases.it.description')"></p>
                <div class="mt-4 bg-light p-3 rounded-3">
                  <p class="fw-semibold text-dark">{{ t('pages./superpowers.use-cases.ai-prefix') }}</p>
                  <p class="mt-2 text-primary font-monospace small">{{ t('pages./superpowers.use-cases.it.ai-highlight') }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <img src="https://placehold.co/600x400/EEF2FF/4F46E5?text=Onboarding示意圖" alt="新客戶 Onboarding 流程示意圖" class="img-fluid rounded-3 shadow-sm">
              </div>
            </div>
          </div> -->
        </div>
      </div>
    </div>
  </section>

   <!-- Capstone AI Feature Section -->
  <section id="ai-analyst" class="section-py bg-white">
    <div class="container">
      <div class="text-center mx-auto" style="max-width: 800px;">
        <h2 class="display-4 fw-bold text-dark">{{ t('pages./superpowers.ai-analyst.title-prefix') }} <span class="gradient-text">{{ t('pages./superpowers.ai-analyst.title-highlight') }}</span></h2>
        <p class="lead text-secondary mt-4">{{ t('pages./superpowers.ai-analyst.description') }}</p>
      </div>
      <div class="mt-5 mx-auto chat-container rounded-4 shadow-lg p-3 p-sm-4" style="max-width: 700px; height: 480px; background-color: #172032;" ref="chatContainer">
        <!-- 終端標題列 -->
        <div class="d-flex align-items-center gap-2 border-bottom border-secondary-subtle pb-3 mb-4">
          <div style="width: 12px; height: 12px;" class="bg-danger rounded-circle"></div>
          <div style="width: 12px; height: 12px;" class="bg-warning rounded-circle"></div>
          <div style="width: 12px; height: 12px;" class="bg-success rounded-circle"></div>
        </div>
        
        <!-- 用戶訊息氣泡 -->
        <div class="message-bubble user-message" :class="{ 'animate-in': showUserMessage }">
          <div class="d-flex justify-content-end">
            <div class="bg-primary text-white px-4 py-3 rounded-4 shadow-sm" style="max-width: 80%;">
              <p class="mb-0">{{ currentDemo.userPrompt }}</p>
            </div>
          </div>
        </div>
        
        <!-- AI 思考中訊息 -->
        <div class="message-bubble ai-thinking" :class="{ 'animate-in': showAiThinking }">
          <div class="d-flex justify-content-start">
            <div class="bg-light text-dark px-4 py-3 rounded-4 shadow-sm" style="max-width: 85%;">
              <span>{{ currentDemo.aiResponse }}</span>
            </div>
          </div>
        </div>
        
        <!-- AI 跳動點訊息 -->
        <div class="message-bubble ai-dots" :class="{ 'animate-in': showAiDots }">
          <div class="d-flex justify-content-start">
            <div class="bg-light text-dark px-4 py-3 rounded-4 shadow-sm" style="min-width: 60px;">
              <span class="thinking-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </div>
          </div>
        </div>
        
        <!-- AI 結果訊息 -->
        <div class="message-bubble ai-result" :class="{ 'animate-in': showAiResult }">
          <div class="d-flex justify-content-start">
            <div class="bg-light text-dark px-4 py-3 rounded-4 shadow-sm" style="max-width: 90%;">
              <p class="fw-semibold text-primary">{{ currentDemo.resultTitle }}</p>
              <div class="d-flex flex-column gap-2">
                <div 
                  v-for="(item, index) in currentDemo.resultItems" 
                  :key="index"
                  class="d-flex justify-content-between"
                >
                  <span class="text-secondary">{{ getItemLabel(item, index) }}</span>
                  <span :class="`text-${item.color} fw-semibold`">{{ item.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- AI 對話情境選擇 tabs -->
      <div class="mt-4">
        <ul class="nav nav-pills justify-content-center flex-wrap gap-2" role="tablist">
          <li class="nav-item" role="presentation">
            <button class="nav-link px-3 py-2 small" :class="{ active: activeAiTab === 'finance' }" @click="switchAiTab('finance')" type="button">
              {{ t('pages./superpowers.use-cases.tabs.finance') }}
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link px-3 py-2 small" :class="{ active: activeAiTab === 'hr' }" @click="switchAiTab('hr')" type="button">
              {{ t('pages./superpowers.use-cases.tabs.hr') }}
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link px-3 py-2 small" :class="{ active: activeAiTab === 'sales' }" @click="switchAiTab('sales')" type="button">
              {{ t('pages./superpowers.use-cases.tabs.sales') }}
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link px-3 py-2 small" :class="{ active: activeAiTab === 'service' }" @click="switchAiTab('service')" type="button">
              {{ t('pages./superpowers.use-cases.tabs.service') }}
            </button>
          </li>
        </ul>
      </div>
    </div>
  </section>

  <!-- More Amazing Features Section -->
  <section id="advanced-features" class="section-py">
    <div class="container">
      <div class="text-center">
        <h2 class="display-5 fw-bold text-dark">{{ t('pages./superpowers.advanced-features.title') }}</h2>
        <p class="lead text-secondary mx-auto mt-3" style="max-width: 650px;">{{ t('pages./superpowers.advanced-features.subtitle') }}</p>
      </div>
      <div class="row g-5 align-items-center mt-5">
        <div class="col-md-6 col-lg-7">
          <HomeMoreFeaturesAiConsultant />
        </div>
        <div class="col-md-6 col-lg-5">
          <h3 class="fs-2 fw-bold mt-3">{{ t('pages./superpowers.advanced-features.ai-consultant.title') }}</h3>
          <p class="lead text-secondary mt-2">{{ t('pages./superpowers.advanced-features.ai-consultant.description') }}</p>
        </div>
      </div>
      <div class="row g-5 align-items-start mt-5">
        <!-- AI 任務處理 - 左欄 -->
        <div class="col-md-6">
          <div class="ai-processing-image-container">
            <img :src="homeAiImg" alt="AI Processing" class="ai-processing-image" />
          </div>
          <h3 class="fs-2 fw-bold mt-3">{{ t('pages./superpowers.advanced-features.ai-processing.title') }}</h3>
          <p class="lead text-secondary mt-2">{{ t('pages./superpowers.advanced-features.ai-processing.description') }}</p>
        </div>
        <!-- 外部發起流程 - 右欄 -->
        <div class="col-md-6">
          <div class="external-forms-image-container">
            <img :src="homeFormImg" alt="External Forms" class="external-forms-image" />
          </div>
          <h3 class="fs-2 fw-bold mt-3">{{ t('pages./superpowers.advanced-features.external-forms.title') }}</h3>
          <p class="lead text-secondary mt-2">{{ t('pages./superpowers.advanced-features.external-forms.description') }}</p>
        </div>
      </div>
    </div>
  </section>
  
  <!-- Advanced Use Cases Section -->
  <section id="advanced-use-cases" class="section-py">
    <div class="container">
      <div class="text-center mb-5">
        <h2 class="display-5 fw-bold text-dark">{{ t('pages./superpowers.advanced-use-cases.title') }}</h2>
        <p class="lead text-secondary mx-auto mt-3" style="max-width: 640px;">{{ t('pages./superpowers.advanced-use-cases.subtitle') }}</p>
      </div>

      <!-- Use Case 1: AI 智能指派 -->
      <div class="use-case-block py-5 border-top">
        <h5 class="fw-bold mb-4 text-center">{{ t('pages./superpowers.advanced-use-cases.ai-assign.title') }}</h5>
        <div class="row g-3 align-items-stretch">
          <!-- 痛點 -->
          <div class="col-md-5">
            <div class="rounded-3 p-4 h-100" style="background: #f8f9fa;">
              <div class="fs-1 mb-2">{{ t('pages./superpowers.advanced-use-cases.ai-assign.pain-emoji') }}</div>
              <p class="text-secondary mb-3">{{ t('pages./superpowers.advanced-use-cases.ai-assign.pain') }}</p>
              <img src="/images/ai-approve-pain.webp" class="img-fluid rounded-2 mt-2" alt="stressed manager buried in paperwork">
            </div>
          </div>
          <!-- 箭頭 -->
          <div class="col-md-2 d-flex align-items-center justify-content-center py-3 py-md-0" style="min-height: 80px;">
            <i class="fas fa-arrow-right fa-2x text-primary d-none d-md-inline"></i>
            <i class="fas fa-arrow-down fa-2x text-primary d-md-none"></i>
          </div>
          <!-- 解決 -->
          <div class="col-md-5">
            <div class="rounded-3 p-4 h-100" style="background: #eff6ff;">
              <div class="fs-1 mb-2">{{ t('pages./superpowers.advanced-use-cases.ai-assign.solution-emoji') }}</div>
              <p class="text-secondary mb-3">{{ t('pages./superpowers.advanced-use-cases.ai-assign.solution') }}</p>
              <!-- 動態流程示意 -->
              <div class="d-flex align-items-center gap-2 flex-wrap mb-3">
                <span class="badge bg-secondary bg-opacity-10 text-secondary">{{ t('pages./superpowers.advanced-use-cases.ai-assign.step1') }}</span>
                <i class="fas fa-arrow-right flow-arrow-animated text-primary"></i>
                <span class="badge bg-primary bg-opacity-10 text-primary"><i class="fas fa-robot me-1"></i>{{ t('pages./superpowers.advanced-use-cases.ai-assign.step2') }}</span>
                <i class="fas fa-arrow-right flow-arrow-animated text-primary" style="animation-delay: 0.75s;"></i>
                <span class="badge bg-success bg-opacity-10 text-success">{{ t('pages./superpowers.advanced-use-cases.ai-assign.step3') }}</span>
              </div>
              <img src="/images/ai-approve-solution.webp" class="img-fluid rounded-2 mt-2" alt="robot and human collaborating on approvals">
            </div>
          </div>
        </div>
      </div>

      <!-- Use Case 2: 外部發起流程 -->
      <div class="use-case-block py-5 border-top">
        <h5 class="fw-bold mb-4 text-center">{{ t('pages./superpowers.advanced-use-cases.external-form.title') }}</h5>
        <div class="row g-3 align-items-stretch">
          <!-- 痛點 -->
          <div class="col-md-5">
            <div class="rounded-3 p-4 h-100" style="background: #f8f9fa;">
              <div class="fs-1 mb-2">{{ t('pages./superpowers.advanced-use-cases.external-form.pain-emoji') }}</div>
              <p class="text-secondary mb-3">{{ t('pages./superpowers.advanced-use-cases.external-form.pain') }}</p>
              <img src="/images/external-trigger-pain.webp" class="img-fluid rounded-2 mt-2" alt="HR overwhelmed by lost emails">
            </div>
          </div>
          <!-- 箭頭 -->
          <div class="col-md-2 d-flex align-items-center justify-content-center py-3 py-md-0" style="min-height: 80px;">
            <i class="fas fa-arrow-right fa-2x text-primary d-none d-md-inline"></i>
            <i class="fas fa-arrow-down fa-2x text-primary d-md-none"></i>
          </div>
          <!-- 解決 -->
          <div class="col-md-5">
            <div class="rounded-3 p-4 h-100" style="background: #f0fdf4;">
              <div class="fs-1 mb-2">{{ t('pages./superpowers.advanced-use-cases.external-form.solution-emoji') }}</div>
              <p class="text-secondary mb-3">{{ t('pages./superpowers.advanced-use-cases.external-form.solution') }}</p>
              <!-- 動態流程示意 -->
              <div class="d-flex align-items-center gap-2 flex-wrap mb-3">
                <span class="badge bg-secondary bg-opacity-10 text-secondary">{{ t('pages./superpowers.advanced-use-cases.external-form.step1') }}</span>
                <i class="fas fa-arrow-right flow-arrow-animated text-success"></i>
                <span class="badge bg-success bg-opacity-10 text-success">{{ t('pages./superpowers.advanced-use-cases.external-form.step2') }}</span>
                <i class="fas fa-arrow-right flow-arrow-animated text-success" style="animation-delay: 0.75s;"></i>
                <span class="badge bg-success bg-opacity-10 text-success">{{ t('pages./superpowers.advanced-use-cases.external-form.step3') }}</span>
              </div>
              <img src="/images/external-trigger-solution.webp" class="img-fluid rounded-2 mt-2" alt="external form submission flowing through structured pipeline">
            </div>
          </div>
        </div>
      </div>

      <!-- Use Case 3: 週期流程 -->
      <div class="use-case-block py-5 border-top">
        <h5 class="fw-bold mb-4 text-center">{{ t('pages./superpowers.advanced-use-cases.recurring.title') }}</h5>
        <div class="row g-3 align-items-stretch">
          <!-- 痛點 -->
          <div class="col-md-5">
            <div class="rounded-3 p-4 h-100" style="background: #f8f9fa;">
              <div class="fs-1 mb-2">{{ t('pages./superpowers.advanced-use-cases.recurring.pain-emoji') }}</div>
              <p class="text-secondary mb-3">{{ t('pages./superpowers.advanced-use-cases.recurring.pain') }}</p>
              <img src="/images/recurring-pain.webp" class="img-fluid rounded-2 mt-2" alt="person frantically reminding colleagues every Friday">
            </div>
          </div>
          <!-- 箭頭 -->
          <div class="col-md-2 d-flex align-items-center justify-content-center py-3 py-md-0" style="min-height: 80px;">
            <i class="fas fa-arrow-right fa-2x text-primary d-none d-md-inline"></i>
            <i class="fas fa-arrow-down fa-2x text-primary d-md-none"></i>
          </div>
          <!-- 解決 -->
          <div class="col-md-5">
            <div class="rounded-3 p-4 h-100" style="background: #eff6ff;">
              <div class="fs-1 mb-2">{{ t('pages./superpowers.advanced-use-cases.recurring.solution-emoji') }}</div>
              <p class="text-secondary mb-3">{{ t('pages./superpowers.advanced-use-cases.recurring.solution') }}</p>
              <!-- 動態流程示意 -->
              <div class="d-flex align-items-center gap-2 flex-wrap mb-3">
                <span class="badge bg-secondary bg-opacity-10 text-secondary">{{ t('pages./superpowers.advanced-use-cases.recurring.step1') }}</span>
                <i class="fas fa-arrow-right flow-arrow-animated text-primary"></i>
                <span class="badge bg-primary bg-opacity-10 text-primary">{{ t('pages./superpowers.advanced-use-cases.recurring.step2') }}</span>
                <i class="fas fa-arrow-right flow-arrow-animated text-primary" style="animation-delay: 0.75s;"></i>
                <span class="badge bg-success bg-opacity-10 text-success">{{ t('pages./superpowers.advanced-use-cases.recurring.step3') }}</span>
              </div>
              <img src="/images/recurring-solution.webp" class="img-fluid rounded-2 mt-2" alt="one-time setup then automatic weekly triggers">
            </div>
          </div>
        </div>
      </div>
      <!-- 未來可繼續新增 Use Case -->

    </div>
  </section>

  <!-- Interactive Demo Section -->
  <section id="demo" class="section-py bg-white">
    <div class="container">
      <div class="text-center mb-5">
        <h2 class="display-5 fw-bold text-dark">{{ t('pages./superpowers.demo.section-title') }}</h2>
        <p class="lead text-secondary mx-auto mt-3" style="max-width: 640px;">{{ t('pages./superpowers.demo.section-subtitle') }}</p>
      </div>

      <!-- 步驟指示器 -->
      <div class="d-flex justify-content-center align-items-center gap-2 mb-4 flex-wrap">
        <button
          class="btn btn-sm demo-step-btn"
          :class="demoSlide === 0 ? 'btn-primary' : 'btn-outline-secondary'"
          @click="goToSlide(0)"
        >① {{ t('pages./superpowers.demo.slide1-label') }}</button>
        <i class="fas fa-arrow-right text-muted" style="font-size: 0.75rem;"></i>
        <button
          class="btn btn-sm demo-step-btn"
          :class="demoSlide === 1 ? 'btn-secondary' : 'btn-outline-secondary'"
          @click="goToSlide(1)"
        >② {{ t('pages./superpowers.demo.slide2-label') }}</button>
        <i class="fas fa-arrow-right text-muted" style="font-size: 0.75rem;"></i>
        <button
          class="btn btn-sm demo-step-btn"
          :class="demoSlide === 2 ? 'btn-analyst' : 'btn-outline-secondary'"
          @click="goToSlide(2)"
        >③ {{ t('pages./superpowers.demo.slide3-label') }}</button>
      </div>

      <div class="mx-auto" style="max-width: 1000px;">

        <!-- Slide 1：SuperAdmin 產生流程（8 phases） -->
        <!-- 注意：v-show 的元素不能帶 d-flex（Bootstrap !important 會蓋掉 display:none） -->
        <!-- position: relative 讓 overlay 只覆蓋這個範圍 -->
        <div v-show="demoSlide === 0" style="position: relative; overflow: hidden; border-radius: 0.375rem;">
        <div class="border rounded-3 shadow-sm d-flex" style="min-height: 520px;">

          <!-- Chat 區域 -->
          <div class="d-flex flex-column flex-grow-1" style="min-width: 0;">
            <!-- 視窗標題列 -->
            <div class="bg-white border-bottom px-3 py-2 d-flex align-items-center gap-2">
              <i class="fas fa-robot text-primary"></i>
              <strong class="text-primary small">{{ t('pages./superpowers.demo.admin.role-name') }}</strong>
            </div>
            <!-- 訊息區域 -->
            <div ref="demoAdminMessages" class="demo-messages flex-grow-1 p-3 overflow-auto" style="background-color: #f8f9fa; height: 440px;">

              <!-- Turn 1：用戶訊息（phase >= 1） -->
              <div v-if="adminPhase >= 1" class="mb-3">
                <div class="d-flex justify-content-end">
                  <div class="card bg-primary text-white" style="max-width: 80%;">
                    <div class="card-body py-2 px-3">
                      <p class="mb-0">{{ t('pages./superpowers.demo.admin.user-message1') }}</p>
                      <small class="opacity-75">{{ adminTs.user1 || '' }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AI typing（phase === 1） -->
              <div v-if="adminPhase === 1" class="mb-3">
                <div class="d-flex justify-content-start">
                  <div class="card bg-white border" style="max-width: 80%;">
                    <div class="card-body py-2 px-3">
                      <div class="d-flex align-items-center">
                        <i class="fas fa-robot text-primary me-2"></i>
                        <strong class="text-primary me-3">{{ t('pages./superpowers.demo.admin.role-name') }}</strong>
                        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                        <span class="ms-2 text-muted small">{{ t('pages./superpowers.demo.thinking') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AI 規劃回覆（phase >= 2） -->
              <div v-if="adminPhase >= 2" class="mb-3">
                <div class="d-flex justify-content-start">
                  <div class="card bg-white border" style="max-width: 85%;">
                    <div class="card-body py-2 px-3">
                      <div class="d-flex align-items-center mb-2">
                        <i class="fas fa-robot text-primary me-2"></i>
                        <strong class="text-primary">{{ t('pages./superpowers.demo.admin.role-name') }}</strong>
                      </div>
                      <p class="mb-2" v-html="t('pages./superpowers.demo.admin.ai-msg1')"></p>
                      <small class="text-muted">{{ adminTs.ai1 || '' }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- System：list_groups（phase >= 3，獨立 phase） -->
              <div v-if="adminPhase >= 3" class="d-flex justify-content-center mb-3">
                <div class="demo-system-container" style="max-width: 90%; width: 100%;">
                  <div class="text-center mb-2">
                    <span class="badge bg-secondary px-3 py-2">
                      <i class="fas fa-cogs me-2"></i>{{ t('pages./superpowers.demo.admin.tool-list-groups') }}
                    </span>
                  </div>
                  <div class="execution-result execution-result-success mb-2">
                    <div class="execution-result-header">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      <strong>{{ t('pages./superpowers.demo.admin.tool-list-groups') }}</strong>
                    </div>
                    <div class="execution-result-details">
                      <small class="text-muted">{{ t('pages./superpowers.demo.admin.tool-list-groups-result') }}</small>
                    </div>
                  </div>
                  <div class="text-center mt-1"><small class="text-muted">{{ adminTs.lg || '' }}</small></div>
                </div>
              </div>

              <!-- AI 建議新增群組（phase >= 4） -->
              <div v-if="adminPhase >= 4" class="mb-3">
                <div class="d-flex justify-content-start">
                  <div class="card bg-white border" style="max-width: 85%;">
                    <div class="card-body py-2 px-3">
                      <div class="d-flex align-items-center mb-2">
                        <i class="fas fa-robot text-primary me-2"></i>
                        <strong class="text-primary">{{ t('pages./superpowers.demo.admin.role-name') }}</strong>
                      </div>
                      <p class="mb-2" v-html="t('pages./superpowers.demo.admin.ai-msg2')"></p>
                      <small class="text-muted">{{ adminTs.ai2 || '' }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Turn 2：用戶訊息（phase >= 5） -->
              <div v-if="adminPhase >= 5" class="mb-3">
                <div class="d-flex justify-content-end">
                  <div class="card bg-primary text-white" style="max-width: 80%;">
                    <div class="card-body py-2 px-3">
                      <p class="mb-0">{{ t('pages./superpowers.demo.admin.user-message2') }}</p>
                      <small class="opacity-75">{{ adminTs.user2 || '' }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AI typing（phase === 5） -->
              <div v-if="adminPhase === 5" class="mb-3">
                <div class="d-flex justify-content-start">
                  <div class="card bg-white border" style="max-width: 80%;">
                    <div class="card-body py-2 px-3">
                      <div class="d-flex align-items-center">
                        <i class="fas fa-robot text-primary me-2"></i>
                        <strong class="text-primary me-3">{{ t('pages./superpowers.demo.admin.role-name') }}</strong>
                        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                        <span class="ms-2 text-muted small">{{ t('pages./superpowers.demo.thinking') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AI 開始建立（phase >= 6） -->
              <div v-if="adminPhase >= 6" class="mb-3">
                <div class="d-flex justify-content-start">
                  <div class="card bg-white border" style="max-width: 85%;">
                    <div class="card-body py-2 px-3">
                      <div class="d-flex align-items-center mb-2">
                        <i class="fas fa-robot text-primary me-2"></i>
                        <strong class="text-primary">{{ t('pages./superpowers.demo.admin.role-name') }}</strong>
                      </div>
                      <p class="mb-2">{{ t('pages./superpowers.demo.admin.ai-msg3') }}</p>
                      <small class="text-muted">{{ adminTs.ai3 || '' }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- System：create_group1（phase >= 7） -->
              <div v-if="adminPhase >= 7" class="d-flex justify-content-center mb-3">
                <div class="demo-system-container" style="max-width: 90%; width: 100%;">
                  <div class="text-center mb-2">
                    <span class="badge bg-secondary px-3 py-2">
                      <i class="fas fa-cogs me-2"></i>{{ t('pages./superpowers.demo.admin.tool-create-group1') }}
                    </span>
                  </div>
                  <div class="execution-result execution-result-success mb-2">
                    <div class="execution-result-header">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      <strong>{{ t('pages./superpowers.demo.admin.tool-create-group1') }}</strong>
                    </div>
                    <div class="execution-result-details">
                      <small class="text-muted">{{ t('pages./superpowers.demo.admin.tool-create-group1-result') }}</small>
                    </div>
                  </div>
                  <div class="text-center mt-1"><small class="text-muted">{{ adminTs.grp1 || '' }}</small></div>
                </div>
              </div>

              <!-- System：create_group2（phase >= 8） -->
              <div v-if="adminPhase >= 8" class="d-flex justify-content-center mb-3">
                <div class="demo-system-container" style="max-width: 90%; width: 100%;">
                  <div class="text-center mb-2">
                    <span class="badge bg-secondary px-3 py-2">
                      <i class="fas fa-cogs me-2"></i>{{ t('pages./superpowers.demo.admin.tool-create-group2') }}
                    </span>
                  </div>
                  <div class="execution-result execution-result-success mb-2">
                    <div class="execution-result-header">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      <strong>{{ t('pages./superpowers.demo.admin.tool-create-group2') }}</strong>
                    </div>
                    <div class="execution-result-details">
                      <small class="text-muted">{{ t('pages./superpowers.demo.admin.tool-create-group2-result') }}</small>
                    </div>
                  </div>
                  <div class="text-center mt-1"><small class="text-muted">{{ adminTs.grp2 || '' }}</small></div>
                </div>
              </div>

              <!-- System：create_workflow（phase >= 9） -->
              <div v-if="adminPhase >= 9" class="d-flex justify-content-center mb-3">
                <div class="demo-system-container" style="max-width: 90%; width: 100%;">
                  <div class="text-center mb-2">
                    <span class="badge bg-secondary px-3 py-2">
                      <i class="fas fa-cogs me-2"></i>{{ t('pages./superpowers.demo.admin.tool-create-workflow') }}
                    </span>
                  </div>
                  <div class="execution-result execution-result-success mb-2">
                    <div class="execution-result-header">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      <strong>{{ t('pages./superpowers.demo.admin.tool-create-workflow') }}</strong>
                    </div>
                    <div class="execution-result-details">
                      <small class="text-muted">{{ t('pages./superpowers.demo.admin.tool-create-workflow-result') }}</small>
                    </div>
                  </div>
                  <div class="text-center mt-1"><small class="text-muted">{{ adminTs.wf || '' }}</small></div>
                </div>
              </div>

              <!-- System：create_workflow_version + View 按鈕（phase >= 10） -->
              <div v-if="adminPhase >= 10" class="d-flex justify-content-center mb-3">
                <div class="demo-system-container" style="max-width: 90%; width: 100%;">
                  <div class="text-center mb-2">
                    <span class="badge bg-secondary px-3 py-2">
                      <i class="fas fa-cogs me-2"></i>{{ t('pages./superpowers.demo.admin.tool-create-workflow-version') }}
                    </span>
                  </div>
                  <div class="execution-result execution-result-success mb-2">
                    <div class="execution-result-header d-flex justify-content-between align-items-center">
                      <div class="d-flex align-items-center">
                        <i class="fas fa-check-circle text-success me-2"></i>
                        <strong>{{ t('pages./superpowers.demo.admin.tool-create-workflow-version') }}</strong>
                      </div>
                      <!-- View 按鈕，未開啟時 pulse -->
                      <button
                        class="btn btn-sm btn-outline-primary"
                        :class="{ 'btn-pulse': !showWorkflowDiagram }"
                        type="button"
                        @click="showWorkflowDiagram = !showWorkflowDiagram"
                      >
                        <i class="fas fa-eye me-1"></i>{{ t('pages./superpowers.demo.admin.view-workflow') }}
                      </button>
                    </div>
                    <div class="execution-result-details">
                      <small class="text-muted">{{ t('pages./superpowers.demo.admin.tool-create-workflow-version-result') }}</small>
                    </div>
                  </div>
                  <div class="text-center mt-1"><small class="text-muted">{{ adminTs.wfv || '' }}</small></div>
                </div>
              </div>

              <!-- AI 詢問是否發佈（phase >= 11） -->
              <div v-if="adminPhase >= 11" class="mb-3">
                <div class="d-flex justify-content-start">
                  <div class="card bg-white border" style="max-width: 85%;">
                    <div class="card-body py-2 px-3">
                      <div class="d-flex align-items-center mb-2">
                        <i class="fas fa-robot text-primary me-2"></i>
                        <strong class="text-primary">{{ t('pages./superpowers.demo.admin.role-name') }}</strong>
                      </div>
                      <p class="mb-2" v-html="t('pages./superpowers.demo.admin.ai-msg4')"></p>
                      <small class="text-muted">{{ adminTs.ai4 || '' }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Turn 3：用戶訊息（phase >= 12） -->
              <div v-if="adminPhase >= 12" class="mb-3">
                <div class="d-flex justify-content-end">
                  <div class="card bg-primary text-white" style="max-width: 80%;">
                    <div class="card-body py-2 px-3">
                      <p class="mb-0">{{ t('pages./superpowers.demo.admin.user-message3') }}</p>
                      <small class="opacity-75">{{ adminTs.user3 || '' }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AI typing（phase === 12） -->
              <div v-if="adminPhase === 12" class="mb-3">
                <div class="d-flex justify-content-start">
                  <div class="card bg-white border" style="max-width: 80%;">
                    <div class="card-body py-2 px-3">
                      <div class="d-flex align-items-center">
                        <i class="fas fa-robot text-primary me-2"></i>
                        <strong class="text-primary me-3">{{ t('pages./superpowers.demo.admin.role-name') }}</strong>
                        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                        <span class="ms-2 text-muted small">{{ t('pages./superpowers.demo.thinking') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- System：publish_and_activate_version（phase >= 13） -->
              <div v-if="adminPhase >= 13" class="d-flex justify-content-center mb-3">
                <div class="demo-system-container" style="max-width: 90%; width: 100%;">
                  <div class="text-center mb-2">
                    <span class="badge bg-secondary px-3 py-2">
                      <i class="fas fa-cogs me-2"></i>{{ t('pages./superpowers.demo.admin.tool-publish') }}
                    </span>
                  </div>
                  <div class="execution-result execution-result-success mb-2">
                    <div class="execution-result-header">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      <strong>{{ t('pages./superpowers.demo.admin.tool-publish') }}</strong>
                    </div>
                    <div class="execution-result-details">
                      <small class="text-muted">{{ t('pages./superpowers.demo.admin.tool-publish-result') }}</small>
                    </div>
                  </div>
                  <div class="text-center mt-1"><small class="text-muted">{{ adminTs.pub || '' }}</small></div>
                </div>
              </div>

              <!-- AI 完成（phase >= 13） -->
              <div v-if="adminPhase >= 13" class="mb-3">
                <div class="d-flex justify-content-start">
                  <div class="card bg-white border" style="max-width: 85%;">
                    <div class="card-body py-2 px-3">
                      <div class="d-flex align-items-center mb-2">
                        <i class="fas fa-robot text-primary me-2"></i>
                        <strong class="text-primary">{{ t('pages./superpowers.demo.admin.role-name') }}</strong>
                      </div>
                      <p class="mb-2" v-html="t('pages./superpowers.demo.admin.ai-final')"></p>
                      <small class="text-muted">{{ adminTs.pub || '' }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 重新嘗試（phase >= 13） -->
              <div v-if="adminPhase >= 13" class="text-center mt-2 mb-2">
                <button class="btn btn-sm btn-outline-secondary" @click="resetAdminDemo">
                  <i class="fas fa-redo me-1"></i>{{ t('pages./superpowers.demo.retry') }}
                </button>
              </div>

            </div>
            <!-- 輸入區域：Turn 1 / Turn 2 / Turn 3（phase 0-2/3/10） -->
            <!-- 輸入區域：Turn1(phase 0-3) / Turn2(phase 4) / Turn3(phase 11)，各自獨立 phase -->
            <div v-if="adminPhase <= 3 || adminPhase === 4 || adminPhase === 11" class="border-top p-3 bg-white">
              <div class="d-flex gap-2">
                <textarea
                  class="form-control"
                  rows="1"
                  disabled
                  style="resize: none;"
                  :value="adminPhase <= 3
                    ? t('pages./superpowers.demo.admin.user-message1')
                    : adminPhase === 4
                      ? t('pages./superpowers.demo.admin.user-message2')
                      : t('pages./superpowers.demo.admin.user-message3')"
                ></textarea>
                <!-- phase 0/4/11 時 pulse -->
                <button
                  class="btn btn-primary"
                  :class="{ 'btn-pulse': adminPhase === 0 || adminPhase === 4 || adminPhase === 11 }"
                  :disabled="adminPhase >= 1 && adminPhase <= 3"
                  @click="adminPhase <= 3 ? sendAdminTurn1() : adminPhase === 4 ? sendAdminTurn2() : sendAdminTurn3()"
                >
                  <i class="fas fa-paper-plane"></i>
                </button>
              </div>
            </div>
          </div>

        </div><!-- /.border.d-flex (inner) -->

        <!-- 流程圖 overlay（position:absolute，僅覆蓋 SuperAdmin 對話框範圍） -->
        <div
          v-if="showWorkflowDiagram"
          class="demo-diagram-overlay"
          @click.self="showWorkflowDiagram = false"
        >
          <div class="demo-diagram-inner">
            <button class="demo-modal-close btn btn-sm btn-outline-secondary" @click="showWorkflowDiagram = false">
              <i class="fas fa-times"></i>
            </button>
            <!-- 隱藏 controls-panel（標題 + 三個按鈕）via :deep() CSS -->
            <div class="demo-visualization-wrapper">
              <WorkflowVisualization :nodes="demoWorkflowNodes" />
            </div>
          </div>
        </div>

        </div><!-- /v-show slide 1 -->

        <!-- Slide 2：模擬員工表單 + 右側流程進度（比照 App WorkflowInstanceDetail） -->
        <div v-show="demoSlide === 1">
          <div class="border rounded-3 shadow-sm overflow-hidden">
            <div class="row g-0">
              <!-- 左側：表單（一個欄位一行） -->
              <div class="col-lg-7 p-4 bg-white">
                <h5 class="fw-bold text-primary mb-4">{{ t('pages./superpowers.demo.form.title') }}</h5>
                <form @submit.prevent="submitFormDemo">
                  <div class="mb-3">
                    <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.demo.form.applicant') }}</label>
                    <input type="text" class="form-control" :value="t('pages./superpowers.demo.form.sample-applicant')" readonly>
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.demo.form.department') }}</label>
                    <input type="text" class="form-control" :value="t('pages./superpowers.demo.form.sample-department')" readonly>
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.demo.form.leave-type') }}</label>
                    <input type="text" class="form-control" :value="t('pages./superpowers.demo.form.sample-leave-type')" readonly>
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.demo.form.start-date') }}</label>
                    <input type="text" class="form-control" :value="t('pages./superpowers.demo.form.sample-start-date')" readonly>
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.demo.form.end-date') }}</label>
                    <input type="text" class="form-control" :value="t('pages./superpowers.demo.form.sample-end-date')" readonly>
                  </div>
                  <div class="mb-4">
                    <label class="form-label fw-medium text-secondary">{{ t('pages./superpowers.demo.form.reason') }}</label>
                    <textarea class="form-control" rows="2" readonly>{{ t('pages./superpowers.demo.form.sample-reason') }}</textarea>
                  </div>
                  <div v-if="!formSubmitted">
                    <button type="submit" class="btn btn-primary w-100 btn-pulse">
                      <i class="fas fa-paper-plane me-2"></i>{{ t('pages./superpowers.demo.form.submit') }}
                    </button>
                  </div>
                  <div v-else>
                    <div class="alert alert-success d-flex align-items-center gap-2 mb-3">
                      <i class="fas fa-check-circle"></i>
                      <span>{{ t('pages./superpowers.demo.form.success') }}</span>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-secondary w-100" @click="resetFormDemo">
                      <i class="fas fa-redo me-1"></i>{{ t('pages./superpowers.demo.retry') }}
                    </button>
                  </div>
                </form>
              </div>

              <!-- 右側：流程進度（比照 WorkflowInstanceDetail 的 progress-timeline） -->
              <div class="col-lg-5 border-start bg-light p-4">
                <h6 class="fw-bold mb-4">
                  <i class="fas fa-route text-success me-2"></i>{{ t('pages./superpowers.demo.form.progress-title') }}
                </h6>
                <div class="progress-timeline">
                  <!-- Node 1：已完成 -->
                  <div class="timeline-item" :class="{ current: !formSubmitted }">
                    <div class="timeline-marker">
                      <i class="fas" :class="formSubmitted ? 'fa-check text-success' : 'fa-circle text-primary'"></i>
                    </div>
                    <div class="timeline-content">
                      <div class="timeline-header">
                        <strong class="small">{{ t('pages./superpowers.demo.admin.node-start') }}</strong>
                      </div>
                      <div class="timeline-body">
                        <span class="badge" :class="formSubmitted ? 'bg-success' : 'bg-primary'">
                          {{ formSubmitted ? t('pages./superpowers.demo.form.status-submitted') : t('pages./superpowers.demo.form.status-filling') }}
                        </span>
                        <div class="text-muted small mt-1">{{ t('pages./superpowers.demo.form.sample-applicant') }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Node 2：等待中（送出後變 current） -->
                  <div class="timeline-item" :class="{ current: formSubmitted }">
                    <div class="timeline-marker">
                      <i class="fas" :class="formSubmitted ? 'fa-spinner fa-spin text-primary' : 'fa-circle text-muted'"></i>
                    </div>
                    <div class="timeline-content">
                      <div class="timeline-header">
                        <strong class="small">{{ t('pages./superpowers.demo.admin.node-approve1') }}</strong>
                      </div>
                      <div class="timeline-body">
                        <span class="badge" :class="formSubmitted ? 'bg-warning text-dark' : 'bg-secondary'">
                          {{ formSubmitted ? t('pages./superpowers.demo.form.status-pending') : t('pages./superpowers.demo.form.status-waiting') }}
                        </span>
                        <div class="text-muted small mt-1">{{ t('pages./superpowers.demo.admin.node-approve1-assignment') }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Node 3：等待 -->
                  <div class="timeline-item">
                    <div class="timeline-marker">
                      <i class="fas fa-circle text-muted"></i>
                    </div>
                    <div class="timeline-content">
                      <div class="timeline-header">
                        <strong class="small">{{ t('pages./superpowers.demo.admin.node-approve2') }}</strong>
                      </div>
                      <div class="timeline-body">
                        <span class="badge bg-secondary">{{ t('pages./superpowers.demo.form.status-waiting') }}</span>
                        <div class="text-muted small mt-1">{{ t('pages./superpowers.demo.admin.node-approve2-assignment') }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Node 4：等待 -->
                  <div class="timeline-item" style="padding-bottom: 0;">
                    <div class="timeline-marker">
                      <i class="fas fa-circle text-muted"></i>
                    </div>
                    <div class="timeline-content">
                      <div class="timeline-header">
                        <strong class="small">{{ t('pages./superpowers.demo.admin.node-notify') }}</strong>
                      </div>
                      <div class="timeline-body">
                        <span class="badge bg-secondary">{{ t('pages./superpowers.demo.form.status-waiting') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Slide 3：SuperAnalyst 查詢分析（紫色） -->
        <div v-show="demoSlide === 2">
        <div class="demo-chat-window border rounded-3 overflow-hidden shadow-sm">
          <!-- 視窗標題列（紫色） -->
          <div class="bg-white border-bottom px-3 py-2 d-flex align-items-center gap-2">
            <i class="fas fa-chart-bar text-analyst"></i>
            <strong class="text-analyst small">{{ t('pages./superpowers.demo.analyst.role-name') }}</strong>
          </div>
          <!-- 訊息區域 -->
          <div ref="demoAnalystMessages" class="demo-messages p-3 overflow-auto" style="background-color: #f8f9fa; height: 360px;">

            <!-- 用戶訊息（phase >= 1） -->
            <div v-if="analystPhase >= 1" class="mb-3">
              <div class="d-flex justify-content-end">
                <div class="card bg-primary text-white" style="max-width: 80%;">
                  <div class="card-body py-2 px-3">
                    <p class="mb-0">{{ t('pages./superpowers.demo.analyst.user-message') }}</p>
                    <small class="opacity-75">{{ analystTs.user || '' }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- AI typing（phase === 1） -->
            <div v-if="analystPhase === 1" class="mb-3">
              <div class="d-flex justify-content-start">
                <div class="card bg-white border" style="max-width: 80%;">
                  <div class="card-body py-2 px-3">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-chart-bar text-analyst me-2"></i>
                      <strong class="text-analyst me-3">{{ t('pages./superpowers.demo.analyst.role-name') }}</strong>
                      <div class="spinner-border spinner-border-sm text-analyst" role="status"></div>
                      <span class="ms-2 text-muted small">{{ t('pages./superpowers.demo.analyzing') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- AI 第一句話（phase >= 2） -->
            <div v-if="analystPhase >= 2" class="mb-3">
              <div class="d-flex justify-content-start">
                <div class="card bg-white border" style="max-width: 80%;">
                  <div class="card-body py-2 px-3">
                    <div class="d-flex align-items-center mb-2">
                      <i class="fas fa-chart-bar text-analyst me-2"></i>
                      <strong class="text-analyst">{{ t('pages./superpowers.demo.analyst.role-name') }}</strong>
                    </div>
                    <p class="mb-2">{{ t('pages./superpowers.demo.analyst.ai-msg1') }}</p>
                    <small class="text-muted">{{ analystTs.ai1 || '' }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- System 訊息：tool call（phase >= 2） -->
            <div v-if="analystPhase >= 2" class="d-flex justify-content-center mb-3">
              <div class="demo-system-container" style="max-width: 90%; width: 100%;">
                <div class="text-center mb-2">
                  <span class="badge bg-info px-3 py-2">
                    <i class="fas fa-database me-2"></i>{{ t('pages./superpowers.demo.analyst.tool-name') }}
                  </span>
                </div>
                <div class="execution-result execution-result-success mb-2">
                  <div class="execution-result-header">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      <strong>{{ t('pages./superpowers.demo.analyst.tool-name') }}</strong>
                    </div>
                  </div>
                  <div class="execution-result-details">
                    <small class="text-muted">{{ t('pages./superpowers.demo.analyst.tool-result') }}</small>
                  </div>
                </div>
                <div class="text-center mt-1">
                  <small class="text-muted">{{ analystTs.ai1 || '' }}</small>
                </div>
              </div>
            </div>

            <!-- AI 最終回覆（phase >= 3） -->
            <div v-if="analystPhase >= 3" class="mb-3">
              <div class="d-flex justify-content-start">
                <div class="card bg-white border" style="max-width: 80%;">
                  <div class="card-body py-2 px-3">
                    <div class="d-flex align-items-center mb-2">
                      <i class="fas fa-chart-bar text-analyst me-2"></i>
                      <strong class="text-analyst">{{ t('pages./superpowers.demo.analyst.role-name') }}</strong>
                    </div>
                    <p class="mb-2" style="white-space: pre-line;">{{ t('pages./superpowers.demo.analyst.ai-msg2') }}</p>
                    <small class="text-muted">{{ analystTs.ai2 || '' }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- 重新嘗試（phase >= 3） -->
            <div v-if="analystPhase >= 3" class="text-center mt-2">
              <button class="btn btn-sm btn-outline-secondary" @click="resetAnalystDemo">
                <i class="fas fa-redo me-1"></i>{{ t('pages./superpowers.demo.retry') }}
              </button>
            </div>

          </div>
          <!-- 輸入區域（紫色 send 按鈕） -->
          <div class="border-top p-3 bg-white">
            <div class="d-flex gap-2">
              <textarea
                class="form-control"
                rows="1"
                disabled
                style="resize: none;"
                :value="t('pages./superpowers.demo.analyst.user-message')"
              ></textarea>
              <!-- analystPhase === 0 時 pulse，引導點擊 -->
              <button
                class="btn btn-analyst"
                :class="{ 'btn-pulse-analyst': analystPhase === 0 }"
                :disabled="analystPhase > 0"
                @click="sendAnalystDemo"
              >
                <i class="fas fa-paper-plane"></i>
              </button>
            </div>
          </div>
        </div><!-- /.demo-chat-window (inner) -->
        </div><!-- /v-show slide 3 -->

      </div>

      <!-- 上下一步導覽 -->
      <div class="d-flex justify-content-center gap-3 mt-4">
        <button class="btn btn-outline-secondary" @click="prevSlide" :disabled="demoSlide === 0">
          <i class="fas fa-arrow-left me-1"></i>{{ t('pages./superpowers.demo.prev') }}
        </button>
        <button class="btn btn-outline-secondary" @click="nextSlide" :disabled="demoSlide === 2">
          {{ t('pages./superpowers.demo.next') }}<i class="fas fa-arrow-right ms-1"></i>
        </button>
      </div>

    </div>
  </section>

  <!-- Final CTA Section -->
  <section id="pricing" class="section-py bg-white">
    <div class="container text-center">
      <h2 class="display-5 fw-bold text-dark">{{ t('pages./superpowers.cta.title') }}</h2>
      <p class="lead text-secondary mt-3 mx-auto" style="max-width: 600px;">{{ t('pages./superpowers.cta.subtitle') }}</p>
      <div class="mt-5">
        <template v-if="!authStore.isLoggedIn">
          <a :href="`/${$i18n.locale}/signup`" class="landing-btn landing-btn-lg">
            {{ t('pages./superpowers.hero.cta') }}
          </a>
        </template>
        <template v-else>
          <a href="/app" class="landing-btn landing-btn-lg">
            {{ t('common.enter-dashboard') }}
          </a>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import HomeMoreFeaturesAiConsultant from '../components/HomeMoreFeaturesAiConsultant.vue'
import HomeMoreFeaturesAiApprove from '../components/HomeMoreFeaturesAiApprove.vue'
import HomeWorkflowChart from '../components/HomeWorkflowChart.vue'
import WorkflowVisualization from '../components/WorkflowVisualization.vue'
// 已合併的 components: CustomerServiceForm, CustomerServiceFlowchart, FinanceExpenseForm, FinanceExpenseFlowchart
import { useIntersectionObserver } from '@vueuse/core'
import homeAiImg from '../assets/images/home-ai.png'
import homeFormImg from '../assets/images/home-form.png'

const { t } = useI18n()
const route = useRoute()
const authStore = useAuthStore()

// Demo carousel 狀態
const demoSlide = ref(0)
// adminPhase: 0=ready
//  Turn1: 1=user+typing 2=ai規劃+list_groups 3=ai群組建議[input2]
//  Turn2: 4=user+typing 5=ai-msg3 6=create_group1 7=create_group2 8=create_workflow 9=create_workflow_version 10=ai-msg4[input3]
//  Turn3: 11=user+typing 12=publish+ai-final[retry]
const adminPhase = ref(0)
const showWorkflowDiagram = ref(false)
const formSubmitted = ref(false)
const analystPhase = ref(0)  // 0:ready 1:user+typing 2:ai+tool 3:final
const demoAdminMessages = ref<HTMLElement>()
const demoAnalystMessages = ref<HTMLElement>()

// 真實時間戳記（key=事件名稱）
const adminTs = ref<Record<string, string>>({})
const analystTs = ref<Record<string, string>>({})
const nowStr = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

const goToSlide = (slide: number) => { demoSlide.value = slide }
const prevSlide = () => { if (demoSlide.value > 0) demoSlide.value-- }
const nextSlide = () => { if (demoSlide.value < 2) demoSlide.value++ }

const scrollDemoToBottom = (el: HTMLElement | undefined) => {
  nextTick(() => { if (el) el.scrollTop = el.scrollHeight })
}

const step = (phase: number, extra?: string) => {
  adminPhase.value = phase
  if (extra) adminTs.value[extra] = nowStr()
  scrollDemoToBottom(demoAdminMessages.value)
}

// Turn 1：送出第一個問題，AI 規劃並查詢群組
const sendAdminTurn1 = () => {
  if (adminPhase.value !== 0) return
  adminTs.value.user1 = nowStr()
  step(1)
  setTimeout(() => step(2, 'ai1'), 1800)   // AI 規劃訊息
  setTimeout(() => step(3, 'lg'), 2500)    // list_groups（獨立 phase，600ms 後）
  setTimeout(() => step(4, 'ai2'), 3600)   // AI 群組建議
}

// Turn 2：送出「go」，各 tool call 逐一出現
const sendAdminTurn2 = () => {
  if (adminPhase.value !== 4) return
  adminTs.value.user2 = nowStr()
  step(5)
  setTimeout(() => step(6, 'ai3'), 1000)
  setTimeout(() => step(7, 'grp1'), 1900)
  setTimeout(() => step(8, 'grp2'), 2800)
  setTimeout(() => step(9, 'wf'), 3700)
  setTimeout(() => step(10, 'wfv'), 4600)
  setTimeout(() => step(11, 'ai4'), 5700)
}

// Turn 3：送出「發佈」
const sendAdminTurn3 = () => {
  if (adminPhase.value !== 11) return
  adminTs.value.user3 = nowStr()
  step(12)
  setTimeout(() => step(13, 'pub'), 1500)
}

const resetAdminDemo = () => {
  adminPhase.value = 0
  adminTs.value = {}
  showWorkflowDiagram.value = false
}

// demo 用的 mock workflow nodes（使用真實 WorkflowVisualization 元件渲染）
const demoWorkflowNodes = computed(() => [
  {
    id: 'node_start',
    name: t('pages./superpowers.demo.admin.node-start'),
    type: 'Start',
    transitions: [{ id: 'e1', destinationNodeId: 'node_approve1' }]
  },
  {
    id: 'node_approve1',
    name: t('pages./superpowers.demo.admin.node-approve1'),
    type: 'process',
    instruction: t('pages./superpowers.demo.admin.node-approve1-assignment'),
    transitions: [{ id: 'e2', destinationNodeId: 'node_approve2' }]
  },
  {
    id: 'node_approve2',
    name: t('pages./superpowers.demo.admin.node-approve2'),
    type: 'process',
    instruction: t('pages./superpowers.demo.admin.node-approve2-assignment'),
    transitions: [{ id: 'e3', destinationNodeId: 'node_end' }]
  },
  {
    id: 'node_end',
    name: t('pages./superpowers.demo.admin.node-notify'),
    type: 'End',
    transitions: []
  }
])

const submitFormDemo = () => { formSubmitted.value = true }
const resetFormDemo = () => { formSubmitted.value = false }

const sendAnalystDemo = () => {
  if (analystPhase.value > 0) return
  analystTs.value.user = nowStr()
  analystPhase.value = 1
  scrollDemoToBottom(demoAnalystMessages.value)
  setTimeout(() => {
    analystTs.value.ai1 = nowStr()
    analystPhase.value = 2
    scrollDemoToBottom(demoAnalystMessages.value)
  }, 1800)
  setTimeout(() => {
    analystTs.value.ai2 = nowStr()
    analystPhase.value = 3
    scrollDemoToBottom(demoAnalystMessages.value)
  }, 3200)
}
const resetAnalystDemo = () => { analystPhase.value = 0; analystTs.value = {} }

// 控制分頁顯示
const activeTab = ref('finance')

// 控制 AI 對話情境
const activeAiTab = ref('finance')

// 控制訊息氣泡動畫
const chatContainer = ref<HTMLElement>()
const showUserMessage = ref(false)
const showAiThinking = ref(false)
const showAiDots = ref(false)
const showAiResult = ref(false)

// 使用 Intersection Observer 偵測滾動
const { stop } = useIntersectionObserver(
  chatContainer,
  ([{ isIntersecting }]) => {
    if (isIntersecting) {
      startAnimation()
      stop() // 只需要觸發一次
    }
  },
  {
    threshold: 0.3 // 當 30% 的區域進入視窗時觸發
  }
)

// AI 對話情境內容
const aiDemoScenarios = computed(() => ({
  finance: {
    userPrompt: t('pages./superpowers.ai-analyst.scenarios.finance.user-prompt'),
    aiResponse: t('pages./superpowers.ai-analyst.scenarios.finance.ai-response'),
    resultTitle: t('pages./superpowers.ai-analyst.scenarios.finance.result-title'),
    resultItems: [
      { label: t('pages./superpowers.ai-analyst.scenarios.finance.dept1'), value: '$124,580', color: 'danger' },
      { label: t('pages./superpowers.ai-analyst.scenarios.finance.dept2'), value: '$98,320', color: 'danger' },
      { label: t('pages./superpowers.ai-analyst.scenarios.finance.dept3'), value: '$76,450', color: 'danger' },
      { label: t('pages./superpowers.ai-analyst.scenarios.finance.dept4'), value: '$45,230', color: 'danger' },
      { label: t('pages./superpowers.ai-analyst.scenarios.finance.dept5'), value: '$32,180', color: 'danger' }
    ]
  },
  hr: {
    userPrompt: t('pages./superpowers.ai-analyst.scenarios.hr.user-prompt'),
    aiResponse: t('pages./superpowers.ai-analyst.scenarios.hr.ai-response'),
    resultTitle: t('pages./superpowers.ai-analyst.scenarios.hr.result-title'),
    resultItems: [
      { label: t('pages./superpowers.ai-analyst.scenarios.hr.total-applications'), value: t('pages./superpowers.ai-analyst.scenarios.hr.total-applications-value'), color: 'success' },
      { label: t('pages./superpowers.ai-analyst.scenarios.hr.first-interview'), value: t('pages./superpowers.ai-analyst.scenarios.hr.first-interview-value'), color: 'info' },
      { label: t('pages./superpowers.ai-analyst.scenarios.hr.second-interview'), value: t('pages./superpowers.ai-analyst.scenarios.hr.second-interview-value'), color: 'warning' },
      { label: t('pages./superpowers.ai-analyst.scenarios.hr.hired'), value: t('pages./superpowers.ai-analyst.scenarios.hr.hired-value'), color: 'success' },
      { label: t('pages./superpowers.ai-analyst.scenarios.hr.hire-rate'), value: '2.3%', color: 'primary' }
    ]
  },
  sales: {
    userPrompt: t('pages./superpowers.ai-analyst.scenarios.sales.user-prompt'),
    aiResponse: t('pages./superpowers.ai-analyst.scenarios.sales.ai-response'),
    resultTitle: t('pages./superpowers.ai-analyst.scenarios.sales.result-title'),
    resultItems: [
      { label: 'Jim', value: '$458,900', color: 'success' },
      { label: 'Dwight', value: '$392,650', color: 'success' },
      { label: 'Stanley', value: '$367,200', color: 'success' },
      { label: 'Phyllis', value: '$285,430', color: 'success' },
      { label: 'Andy', value: '$241,880', color: 'success' }
    ]
  },
  service: {
    userPrompt: t('pages./superpowers.ai-analyst.scenarios.service.user-prompt'),
    aiResponse: t('pages./superpowers.ai-analyst.scenarios.service.ai-response'),
    resultTitle: t('pages./superpowers.ai-analyst.scenarios.service.result-title'),
    resultItems: [
      { label: t('pages./superpowers.ai-analyst.scenarios.service.july-avg'), value: t('pages./superpowers.ai-analyst.scenarios.service.july-avg-value'), color: 'info' },
      { label: t('pages./superpowers.ai-analyst.scenarios.service.august-avg'), value: t('pages./superpowers.ai-analyst.scenarios.service.august-avg-value'), color: 'info' }
    ]
  }
}))

// 當前 AI 對話內容
const currentDemo = computed(() => {
  return aiDemoScenarios.value[activeAiTab.value] || aiDemoScenarios.value.finance
})

// 取得項目標籤（為業務排名添加序號）
const getItemLabel = (item: any, index: number) => {
  if (activeAiTab.value === 'sales') {
    return `${index + 1}. ${item.label}`
  }
  return item.label
}

// 切換 AI 對話情境
const switchAiTab = (tab: string) => {
  // 立即重置動畫狀態，避免看到內容變化
  resetAnimation()
  // 切換tab
  activeAiTab.value = tab
  // 重新開始動畫
  setTimeout(() => startAnimation(), 100)
}

// 重置動畫狀態
const resetAnimation = () => {
  showUserMessage.value = false
  showAiThinking.value = false
  showAiDots.value = false
  showAiResult.value = false
}

// 開始動畫序列
const startAnimation = () => {
  setTimeout(() => showUserMessage.value = true, 300)
  setTimeout(() => showAiThinking.value = true, 1000)
  setTimeout(() => showAiDots.value = true, 1800)
  setTimeout(() => {
    showAiDots.value = false
    showAiResult.value = true
  }, 4000)
}

const availableLanguages = ['en', 'zh-tw', 'zh-cn', 'ja']

const getRoutePath = (path: string) => {
  const lang = route.params.lang
  if (lang && availableLanguages.includes(lang as string)) {
    return path === '/' ? `/${lang}` : `/${lang}${path}`
  }
  return path
}
</script>

<style scoped>

/* ===== Demo Section ===== */

/* SuperAnalyst 紫色主題 */
.text-analyst { color: #7c3aed; }
.btn-analyst {
  background-color: #7c3aed;
  border-color: #7c3aed;
  color: #fff;
}
.btn-analyst:hover:not(:disabled) {
  background-color: #6d28d9;
  border-color: #6d28d9;
  color: #fff;
}
.btn-analyst:disabled {
  background-color: #7c3aed;
  border-color: #7c3aed;
  color: #fff;
  opacity: 0.65;
}
.spinner-border.text-analyst { color: #7c3aed !important; }

/* Demo 步驟按鈕 */
.demo-step-btn { min-width: 120px; }

/* 流程圖 overlay（只覆蓋 SuperAdmin 對話框範圍） */
.demo-diagram-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 10;
  display: flex;       /* flex column 讓 inner div 撐滿高度 */
  flex-direction: column;
  padding: 1.5rem;
}
.demo-diagram-inner {
  position: relative;
  background: white;
  border-radius: 0.5rem;
  width: 100%;
  flex: 1;             /* 撐滿 overlay 剩餘高度 */
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.demo-modal-close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 10;
}
/* visualization wrapper 也用 flex 撐滿 inner，避免 flow-container 高度鏈斷裂 */
.demo-visualization-wrapper {
  flex: 1;
  min-height: 0;
}
.demo-visualization-wrapper :deep(.workflow-visualization) { height: 100%; }
/* 隱藏 controls panel（標題 + 三個操作按鈕） */
.demo-visualization-wrapper :deep(.controls-panel) { display: none; }
.demo-visualization-wrapper :deep(.flow-container) { height: 100% !important; }


/* Slide 2 進度 timeline（比照 WorkflowInstanceDetail） */
.progress-timeline {
  position: relative;
  padding-left: 2rem;
}
.timeline-item {
  position: relative;
  padding-bottom: 1.5rem;
}
.timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: calc(-1.5rem + 0.5rem - 1px);
  top: 1.2rem;
  bottom: 0;
  width: 2px;
  background-color: #dee2e6;
}
.timeline-marker {
  position: absolute;
  left: -2rem;
  top: 0.15rem;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  background: white;
  border-radius: 50%;
}
.timeline-item.current .timeline-marker { color: var(--bs-primary); }
.timeline-content { padding-left: 0.5rem; }
.timeline-header { margin-bottom: 0.3rem; }
.timeline-body .badge { font-size: 0.7rem; }

/* 可點擊按鈕的 pulse 提示動畫 */
@keyframes btn-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.5); }
  50% { box-shadow: 0 0 0 6px rgba(13, 110, 253, 0); }
}
@keyframes btn-pulse-analyst {
  0%, 100% { box-shadow: 0 0 0 0 rgba(124, 58, 237, 0.5); }
  50% { box-shadow: 0 0 0 6px rgba(124, 58, 237, 0); }
}
.btn-pulse {
  animation: btn-pulse 1.4s ease-in-out infinite;
}
.btn-pulse-analyst {
  animation: btn-pulse-analyst 1.4s ease-in-out infinite;
}

/* 系統執行結果樣式（比照 app/super-admin） */
.demo-system-container { width: 100%; }

.execution-result {
  border: 1px solid #e0e0e0;
  border-radius: 0.375rem;
  padding: 0.75rem;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.execution-result-success {
  border-left: 4px solid #28a745;
  background-color: #f8fff9;
}
.execution-result-header {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #155724;
}
.execution-result-details {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #f0f0f0;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  line-height: 1.4;
}

/* Use-cases flowchart 節點：藍色（一般節點）& 紫色（AI 節點） */
.flowchart-node-blue {
  background-color: #dbeafe !important;
  border-color: #3b82f6 !important;
}
.flowchart-node-blue .icon-wrapper {
  background-color: #3b82f6 !important;
}
.flowchart-node-blue h5 { color: #1e40af !important; }

.flowchart-node-purple {
  background-color: #ede9fe !important;
  border-color: #7c3aed !important;
}
.flowchart-node-purple .icon-wrapper {
  background-color: #7c3aed !important;
}
.flowchart-node-purple h5 { color: #5b21b6 !important; }

/* 動態流程箭頭 */
@keyframes flow-pulse {
  0%, 100% { opacity: 0.3; transform: translateX(-2px); }
  50% { opacity: 1; transform: translateX(3px); }
}
.flow-arrow-animated {
  display: inline-block;
  font-size: 0.75rem;
  animation: flow-pulse 1.5s ease-in-out infinite;
}

/* 合併的 FinanceExpenseForm 樣式 */
.finance-expense-form-preview {
  max-width: 100%;
}

.form-control:read-only,
.form-select:disabled {
  background-color: #f8f9fa;
  border-color: #e9ecef;
}

/* 合併的 FinanceExpenseFlowchart 樣式 */
.finance-expense-flowchart .flowchart-container > .flowchart-node {
  min-width: 0;
}

@media (max-width: 767.98px) {
  .finance-expense-flowchart .flowchart-container {
    max-width: 320px;
    margin: 0 auto;
  }
  
  .finance-expense-flowchart .flowchart-node {
    width: 100%;
  }
}

/* 合併的 CustomerServiceFlowchart 樣式 */
.customer-service-flowchart .flowchart-container > .flowchart-node {
  min-width: 0;
}

@media (max-width: 767.98px) {
  .customer-service-flowchart .flowchart-container {
    max-width: 320px;
    margin: 0 auto;
  }
  
  .customer-service-flowchart .flowchart-node {
    width: 100%;
  }
}

/* 合併的 HRApplicationForm 樣式 */
.hr-application-form-preview {
  max-width: 100%;
}

/* 合併的 HRRecruitmentFlowchart 樣式 */
.hr-recruitment-flowchart .flowchart-container > .flowchart-node {
  min-width: 0;
}

@media (max-width: 767.98px) {
  .hr-recruitment-flowchart .flowchart-container {
    max-width: 320px;
    margin: 0 auto;
  }
  
  .hr-recruitment-flowchart .flowchart-node {
    width: 100%;
  }
}

/* 業務報價流程圖樣式 */
.sales-quote-flowchart .flowchart-container > .flowchart-node {
  min-width: 0;
}

@media (max-width: 767.98px) {
  .sales-quote-flowchart .flowchart-container {
    max-width: 320px;
    margin: 0 auto;
  }
  
  .sales-quote-flowchart .flowchart-node {
    width: 100%;
  }
}

/* 業務報價表單樣式 */
.sales-quote-form-preview {
  max-width: 100%;
}

/* AI 任務處理圖片樣式 */
.ai-processing-image-container {
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 12px;
}

.ai-processing-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 外部表單圖片樣式 */
.external-forms-image-container {
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 12px;
}

.external-forms-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
